import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import {
  getNotifications,
  getNotification,
  getUnreadNotificationCount,
  markNotificationRead,
  markAllNotificationsRead,
  getGroupedNotifications,
  markAlertRead,
  getAlertDetails,
  getRulesWithNotificationStats,
  type RuleWithNotificationStats,
} from '@/services/api'
import { useAuthContext } from '@/components/auth/AuthProvider'
import { queryKeys } from '@/hooks/useQueryKeys'
import type { Notification, TimelineNotification, AlertDetail, PaginationParams } from '@/types'

/**
 * useNotifications Hook (T501)
 * Provides notification list, mark read, and unread count functionality.
 *
 * Based on FLOW-04-NOTIFICATIONS.md
 * Uses user-scoped query keys for proper cache isolation between users.
 */

export interface UseNotificationsParams extends PaginationParams {
  unreadOnly?: boolean
}

export interface UseNotificationsReturn {
  notifications: Notification[]
  isLoading: boolean
  error: Error | null
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
  refetch: () => void
}

export function useNotifications(
  params: UseNotificationsParams = {}
): UseNotificationsReturn {
  const { user } = useAuthContext()
  const userId = user?.id ?? null
  const { page = 1, limit = 20, unreadOnly = false } = params

  const query = useQuery({
    queryKey: queryKeys.notifications.list(userId, { page, limit, unreadOnly }),
    queryFn: () => getNotifications({ page, limit, unreadOnly }),
    enabled: !!user,
  })

  const pagination = query.data?.pagination ?? { page, limit, total: 0 }

  return {
    notifications: query.data?.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    pagination: {
      ...pagination,
      totalPages: Math.ceil(pagination.total / pagination.limit),
    },
    refetch: query.refetch,
  }
}

/**
 * useNotification Hook
 * Fetches a single notification by ID.
 */
export interface UseNotificationReturn {
  notification: Notification | null
  isLoading: boolean
  error: Error | null
}

export function useNotification(id: string | undefined): UseNotificationReturn {
  const { user } = useAuthContext()

  const query = useQuery({
    queryKey: ['notification', id],
    queryFn: () => getNotification(id!),
    enabled: !!id && !!user,
  })

  return {
    notification: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
  }
}

/**
 * useUnreadCount Hook
 * Returns the count of unread notifications for the badge.
 */
export interface UseUnreadCountReturn {
  count: number
  isLoading: boolean
  error: Error | null
  refetch: () => void
}

export function useUnreadCount(): UseUnreadCountReturn {
  const { user } = useAuthContext()
  const userId = user?.id ?? null

  const query = useQuery({
    queryKey: queryKeys.notifications.unreadCount(userId),
    queryFn: getUnreadNotificationCount,
    refetchInterval: 60000, // Refetch every minute as fallback
    enabled: !!user,
  })

  return {
    count: query.data ?? 0,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  }
}

/**
 * useMarkNotificationRead Hook
 * Mutation for marking a single notification as read.
 */
export interface UseMarkNotificationReadReturn {
  markAsRead: (id: string) => Promise<void>
  isPending: boolean
  error: Error | null
}

export function useMarkNotificationRead(): UseMarkNotificationReadReturn {
  const queryClient = useQueryClient()
  const { user } = useAuthContext()
  const userId = user?.id ?? null

  const mutation = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => {
      // Invalidate notifications list and unread count (user-scoped)
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all(userId) })
    },
  })

  return {
    markAsRead: async (id: string) => {
      await mutation.mutateAsync(id)
    },
    isPending: mutation.isPending,
    error: mutation.error,
  }
}

/**
 * useMarkAllNotificationsRead Hook
 * Mutation for marking all notifications as read.
 */
export interface UseMarkAllNotificationsReadReturn {
  markAllAsRead: () => Promise<void>
  isPending: boolean
  error: Error | null
}

export function useMarkAllNotificationsRead(): UseMarkAllNotificationsReadReturn {
  const queryClient = useQueryClient()
  const { user } = useAuthContext()
  const userId = user?.id ?? null

  const mutation = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => {
      // Invalidate notifications list and unread count (user-scoped)
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all(userId) })
    },
  })

  return {
    markAllAsRead: async () => {
      await mutation.mutateAsync()
    },
    isPending: mutation.isPending,
    error: mutation.error,
  }
}

/**
 * useGroupedNotifications Hook
 * Provides grouped notifications list (grouped by publication_id + notification_rule_id).
 * Used by Dashboard and Notifications pages for timeline display.
 * Supports filtering by status (all/unread/read), date range, and rule IDs.
 */
export interface UseGroupedNotificationsParams extends PaginationParams {
  unreadOnly?: boolean
  readOnly?: boolean
  ruleIds?: string[]
  dateFrom?: string  // ISO date YYYY-MM-DD
  dateTo?: string    // ISO date YYYY-MM-DD
}

export interface UseGroupedNotificationsReturn {
  notifications: TimelineNotification[]
  isLoading: boolean
  isFetching: boolean
  isPlaceholderData: boolean
  error: Error | null
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  } | null
  refetch: () => void
}

export function useGroupedNotifications(
  params: UseGroupedNotificationsParams = {}
): UseGroupedNotificationsReturn {
  const { user } = useAuthContext()
  const userId = user?.id ?? null
  const { page = 1, limit = 20, unreadOnly = false, readOnly = false, ruleIds, dateFrom, dateTo } = params

  const query = useQuery({
    queryKey: ['grouped-notifications', userId, { page, limit, unreadOnly, readOnly, ruleIds, dateFrom, dateTo }],
    queryFn: () => getGroupedNotifications({ page, limit, unreadOnly, readOnly, ruleIds, dateFrom, dateTo }),
    enabled: !!user,
    placeholderData: keepPreviousData,
  })

  const pagination = query.data?.pagination ?? null

  return {
    notifications: query.data?.data ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    error: query.error,
    pagination: pagination ? {
      ...pagination,
      totalPages: Math.ceil(pagination.total / pagination.limit),
    } : null,
    refetch: query.refetch,
  }
}

/**
 * useRulesWithNotificationStats Hook
 * Provides notification rules with alert statistics for filter dropdown.
 * Returns rules that have at least 1 alert, sorted by most recent alert.
 */
export interface UseRulesWithNotificationStatsReturn {
  rules: RuleWithNotificationStats[]
  isLoading: boolean
  error: Error | null
  refetch: () => void
}

export function useRulesWithNotificationStats(): UseRulesWithNotificationStatsReturn {
  const { user } = useAuthContext()
  const userId = user?.id ?? null

  const query = useQuery({
    queryKey: ['rules-with-notification-stats', userId],
    queryFn: getRulesWithNotificationStats,
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // Consider fresh for 5 minutes
  })

  return {
    rules: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  }
}

/**
 * useMarkGroupedNotificationRead Hook
 * Mutation for marking an alert as read.
 * Uses alert_id directly (the new schema).
 */
export interface UseMarkGroupedNotificationReadReturn {
  /** Mark an alert as read by alert_id */
  markAsRead: (alertId: string) => Promise<void>
  isPending: boolean
  error: Error | null
}

export function useMarkGroupedNotificationRead(): UseMarkGroupedNotificationReadReturn {
  const queryClient = useQueryClient()
  const { user } = useAuthContext()
  const userId = user?.id ?? null

  const mutation = useMutation({
    mutationFn: (alertId: string) => markAlertRead(alertId),
    onSuccess: () => {
      // Invalidate both grouped and regular notifications, plus unread count
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all(userId) })
      queryClient.invalidateQueries({ queryKey: ['grouped-notifications'] })
      queryClient.invalidateQueries({ queryKey: ['notification-rules'] })
    },
  })

  return {
    markAsRead: async (alertId: string) => {
      await mutation.mutateAsync(alertId)
    },
    isPending: mutation.isPending,
    error: mutation.error,
  }
}

/**
 * useAlertDetails Hook
 * Fetches detailed information for an alert by alert_id.
 * Returns all channel notifications, delivery attempts, and metadata.
 */
export interface UseAlertDetailsReturn {
  alertDetails: AlertDetail | null
  isLoading: boolean
  error: Error | null
  refetch: () => void
}

export function useAlertDetails(alertId: string | undefined): UseAlertDetailsReturn {
  const { user } = useAuthContext()

  const query = useQuery({
    queryKey: ['alert-details', alertId],
    queryFn: () => getAlertDetails(alertId!),
    enabled: !!alertId && !!user,
  })

  return {
    alertDetails: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  }
}
